//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import { IQService, IScope, ITimeoutService } from 'angular';
import _ from 'underscore';
import { INgRedux } from 'ng-redux';
import { IReel } from '@galleries/models/IReel';
import { REQUEST_RIGHTS } from '@legacy-components/rights-management/request-rights-constants';
import { requestRightsActions } from './request-rights-actions';
import { ITrackingService } from '@services/tracking-service';
import { TRACKING_ACTION } from '@tracker/trackerModel';
import { SessionVarsService } from '@services/session-vars-service';
import { FeatureNames } from '@bvengineering/curalate-features';
import { isUndefined, isDefined } from '@utils/filterPredicates';
import isValidUrl from '@utils/urlValidation';
import { retailerDefaultMessages } from 'app/constants/rights-management';
import { termsConditionsSettings } from 'app/constants/rights-management/terms-and-conditions-settings';
import { Locales } from 'app/constants/locales/locale';
import { getMessageWithoutUrl } from '@legacy-components/rights-management/request-rights-util';
import rightsConfigurationModalTermsAndConditionsTemplate from '@legacy-components/rights-management/rights-configuration-modal-terms-and-conditions.html';
import rightsConfigurationModalTemplate from '@legacy-components/rights-management/rights-configuration-modal.html';
interface IScopeWithModel extends IScope {
  model: any;
  close: (res: boolean) => void;
}
interface IError {
  [key: string]: any;
}

const curalate = angular.module('curalate');
const ENTER_KEY_CODE = 13;

curalate.directive('rightsConfigurationModal', [
  '$q',
  '$ngRedux',
  '$timeout',
  'ErrorNotificationService',
  'FanreelService',
  'RightsManagementService',
  'ScrollAnimationService',
  'StringUtils',
  'TrackingService',
  'SessionVarsService',
  (
    $q: IQService,
    $ngRedux: INgRedux,
    $timeout: ITimeoutService,
    ErrorNotificationService,
    FanreelService,
    RightsManagementService,
    ScrollAnimationService,
    StringUtils,
    TrackingService: ITrackingService,
    SessionVarsService: SessionVarsService,
  ) => {
    const templateImport = SessionVarsService.isFeatureEnabled(
      FeatureNames.RightsRequestsTermsAndConditions,
    )
      ? rightsConfigurationModalTermsAndConditionsTemplate
      : rightsConfigurationModalTemplate;

    return {
      scope: {
        close: '&',
      },
      restrict: 'E',
      template: templateImport,
      link(scope: IScopeWithModel, element) {
        const LIST_VIEW = 'list';
        const EDIT_VIEW = 'edit';
        const CLIENT_TARGET_TYPE = 'Client';
        const FANREEL_TARGET_TYPE = 'Fanreel';
        const DEFAULT_MESSAGE_LIMIT = 5;

        const defaultEditState = {
          isFanreelConfiguration: false,
          selectedFanreel: null,
          errors: {
            emptyName: false,
          },
        };

        const defaultMessageEditState = {
          isNew: false,
          index: null,
          message: null,
          approvalPhrase: '',
          hashtags: [],
          errors: {
            emptyMessage: false,
            noMessages: false,
            emptyApprovalPhrase: false,
          },
        };

        const defaultTermsEditState = {
          errors: {
            hasError: false,
            errorMessage: null,
          },
        };

        const termsAndConditionsConstants = {
          EDIT_TS_AND_CS_VIEW: 'editTermsAndConditions',
          DEFAULT_URL: 'https://bv-url.com/vc-tou',
          TS_AND_CS_SETUP_BUTTON_LABEL: 'Setup Terms and Conditions',
          TS_AND_CS_EDIT_BUTTON_LABEL: 'Edit Terms and Conditions',
          TS_AND_CS_VIEW_BUTTON_LABEL: 'View Terms and Conditions',
        };

        const termsAndConditionsHelpers = {
          url: undefined,
          customUrl: undefined,
          termsEditState: angular.copy(defaultTermsEditState),
          termsAndConditionsButtonValue:
            termsAndConditionsConstants.TS_AND_CS_SETUP_BUTTON_LABEL,
          isDefaultUrl: false,
        };

        const isBazaarvoiceNetworkSyndicationEnabled =
          SessionVarsService.isFeatureEnabled(
            FeatureNames.BazaarvoiceNetworkSyndicationFrontend,
          );
        const isRightsRequestsTermsAndConditionsEnabled =
          SessionVarsService.isFeatureEnabled(
            FeatureNames.RightsRequestsTermsAndConditions,
          );
        const isRightsRequestsThirdPartyEnabled =
          SessionVarsService.isFeatureEnabled(
            FeatureNames.RightsRequestsThirdParty,
          );
        const isSyndicationAndTermsConditionsEnabled =
          isBazaarvoiceNetworkSyndicationEnabled &&
          isRightsRequestsTermsAndConditionsEnabled;
        scope.model = {
          constants: {
            DEFAULT_MESSAGE_LIMIT,
            LIST_VIEW,
            EDIT_VIEW,
            CLIENT_TARGET_TYPE,
            FANREEL_TARGET_TYPE,
            ...termsAndConditionsConstants,
          },
          outstandingAsyncAction: null,
          currentView: LIST_VIEW,
          configurations: [],
          selectedMessagesCount: 0,
          loading: true,
          isTandCSetup: false,
          oldTandCurl: null,
          currentConfiguration: null,
          editState: angular.copy(defaultEditState),
          messageBeingEdited: false,
          messageEditState: angular.copy(defaultMessageEditState),
          fanreels: null,
          isBazaarvoiceNetworkSyndicationEnabled,
          isRightsRequestsThirdPartyEnabled,
          languageCode: Locales.EN_US,
          termsConditionsSettings,
          retailerMessages:
            termsConditionsSettings[Locales.EN_US].retailerMessages,
          ...termsAndConditionsHelpers,
        };

        scope.model.termsConditionsSettingsList = _.keys(
          scope.model.termsConditionsSettings,
        );

        scope.$watch('model.url', () => {
          scope.model.isDefaultUrl = scope.model.constants.DEFAULT_URL.contains(
            scope.model.url,
          );
        });

        const bvNetworkConfig = [
          {
            message: isSyndicationAndTermsConditionsEnabled
              ? `${REQUEST_RIGHTS.BV_NETWORK_MSG_1} ${
                  termsConditionsSettings[Locales.EN_US].retailerMessages[0]
                } ${termsAndConditionsConstants.DEFAULT_URL}`
              : `${REQUEST_RIGHTS.BV_NETWORK_MSG_1} ${termsAndConditionsConstants.DEFAULT_URL}`,
            approvalPhrase: '#agree',
            languageCode: 'en-us',
          },
          {
            message: isSyndicationAndTermsConditionsEnabled
              ? `${REQUEST_RIGHTS.BV_NETWORK_MSG_2} ${
                  termsConditionsSettings[Locales.EN_US].retailerMessages[1]
                } ${termsAndConditionsConstants.DEFAULT_URL}`
              : `${REQUEST_RIGHTS.BV_NETWORK_MSG_2} ${termsAndConditionsConstants.DEFAULT_URL}`,
            approvalPhrase: '#agree',
            languageCode: 'en-us',
          },
          {
            message: isSyndicationAndTermsConditionsEnabled
              ? `${REQUEST_RIGHTS.BV_NETWORK_MSG_3} ${
                  termsConditionsSettings[Locales.EN_US].retailerMessages[2]
                } ${termsAndConditionsConstants.DEFAULT_URL}`
              : `${REQUEST_RIGHTS.BV_NETWORK_MSG_3} ${termsAndConditionsConstants.DEFAULT_URL}`,

            approvalPhrase: '#agree',
            languageCode: 'en-us',
          },
          {
            message: isSyndicationAndTermsConditionsEnabled
              ? `${REQUEST_RIGHTS.BV_NETWORK_MSG_4} ${
                  termsConditionsSettings[Locales.EN_US].retailerMessages[3]
                } ${termsAndConditionsConstants.DEFAULT_URL}`
              : `${REQUEST_RIGHTS.BV_NETWORK_MSG_4} ${termsAndConditionsConstants.DEFAULT_URL}`,
            approvalPhrase: '#agree',
            languageCode: 'en-us',
          },
          {
            message: isSyndicationAndTermsConditionsEnabled
              ? `${REQUEST_RIGHTS.BV_NETWORK_MSG_5} ${
                  termsConditionsSettings[Locales.EN_US].retailerMessages[1]
                } ${termsAndConditionsConstants.DEFAULT_URL}`
              : `${REQUEST_RIGHTS.BV_NETWORK_MSG_5} ${termsAndConditionsConstants.DEFAULT_URL}`,
            approvalPhrase: '#agree',
            languageCode: 'en-us',
          },
        ];
        //
        // Internal Helpers
        //

        function setErrors() {
          const name = (scope.model.currentConfiguration?.name || '').trim();
          const configuration = scope.model.currentConfiguration;
          let errorsFound = false;

          resetErrorState();

          if (name.length === 0) {
            scope.model.editState.errors.emptyName = true;
            errorsFound = true;
          }

          if (
            !configurationHasMessages(configuration) ||
            configuration?.rightsRequestData.data.length === 0
          ) {
            scope.model.messageEditState.errors.noMessages = true;
            errorsFound = true;
          }

          return errorsFound;
        }

        // Validates the current message edit state and sets errors when appropriate
        function setMessageErrors() {
          const message = (scope.model.messageEditState.message || '').trim();
          let errorsFound = false;

          // Reset error state
          resetErrorState();

          if (message.length === 0) {
            scope.model.messageEditState.errors.emptyMessage = true;
            errorsFound = true;
          }

          if (!scope.model.messageEditState.approvalPhrase) {
            scope.model.messageEditState.errors.emptyApprovalPhrase = true;
            errorsFound = true;
          }

          return errorsFound;
        }

        // Returns true if the configuration has an array of messages already set
        function configurationHasMessages(configuration) {
          return (
            configuration &&
            configuration.rightsRequestData &&
            configuration.rightsRequestData.data
          );
        }

        // Loads the "edit" view for a specified configuration
        function loadAddEditView(configuration) {
          scope.model.editState = _.defaults(
            {
              isFanreelConfiguration:
                scope.model.isConfigurationFanreelType(configuration),
              selectedFanreel: _(scope.model.fanreelsAndCanvases).findWhere({
                id: configuration.entityId,
              }),
            },
            defaultEditState,
          );
          scope.model.currentConfiguration = configuration;
          scope.model.currentView = EDIT_VIEW;
        }

        function loadAddTermsAndConditionsView() {
          scope.model.currentView = scope.model.constants.EDIT_TS_AND_CS_VIEW;
          if (
            isUndefined(scope.model.url) ||
            scope.model.url === null ||
            !scope.model.isCustom
          ) {
            scope.model.url = scope.model.constants.DEFAULT_URL;
          }
          if (
            isUndefined(scope.model.isCustom) ||
            scope.model.isCustom === null
          ) {
            scope.model.isCustom = false;
          }
        }

        // Given a message, extracts and sets the hashtags and therefore changes the state of the approval phrase
        // chooser
        function updateApprovalPhraseState(message) {
          scope.model.messageEditState.hashtags = _(
            StringUtils.extractHashtags(message) || [],
          ).unique();

          if (scope.model.messageEditState.hashtags.length === 0) {
            scope.model.messageEditState.approvalPhrase = null;
          } else if (scope.model.messageEditState.hashtags.length === 1) {
            scope.model.messageEditState.approvalPhrase =
              scope.model.messageEditState.hashtags[0];
          } else {
            // If there are multiple hashtags and the current approval phrase isn't among them, default to the first
            // hashtag.
            // This occurs when a user copy+pastes an approval phrase with multiple hashtags in
            scope.model.messageEditState.approvalPhrase = _(
              scope.model.messageEditState.hashtags,
            ).find((hashtag) => {
              return hashtag === scope.model.messageEditState.approvalPhrase;
            });

            if (!scope.model.messageEditState.approvalPhrase) {
              scope.model.messageEditState.approvalPhrase =
                scope.model.messageEditState.hashtags[0];
            }
          }
        }

        function resetErrorState() {
          scope.model.editState.errors = {};
          scope.model.messageEditState.errors = {};
        }

        // Clean the state
        function resetEditState() {
          const persistedFanreelData = {
            isFanreelConfiguration:
              scope.model.editState.isFanreelConfiguration,
            selectedFanreel: scope.model.editState.selectedFanreel,
          };

          scope.model.messageBeingEdited = false;
          scope.model.languageCode = Locales.EN_US;
          scope.model.messageEditState = angular.copy(defaultMessageEditState);
          scope.model.editState = _(angular.copy(defaultEditState)).extend(
            persistedFanreelData,
          );
        }

        function saveCurrentConfiguration() {
          const configuration = scope.model.currentConfiguration;

          if (setErrors()) {
            return undefined;
          }

          if (scope.model.editState.isFanreelConfiguration) {
            configuration.targetType = FANREEL_TARGET_TYPE;

            if (!scope.model.editState.selectedFanreel) {
              configuration.entityId = scope.model.fanreels[0].id;
            } else {
              configuration.entityId = scope.model.editState.selectedFanreel.id;
            }
          } else {
            configuration.targetType = CLIENT_TARGET_TYPE;
            configuration.entityId = null;
          }

          scope.model.outstandingAsyncAction = 'update';
          const returnPromise =
            RightsManagementService.updateRightsConfigurationAsync(
              configuration,
            )
              .then((freshConfiguration) => {
                const existingConfiguration = _(
                  scope.model.configurations,
                ).findWhere({ id: freshConfiguration.id });

                freshConfiguration.isNew = false;

                if (!existingConfiguration) {
                  scope.model.configurations.push(freshConfiguration);
                } else {
                  (existingConfiguration as any).rightsRequestData =
                    freshConfiguration.rightsRequestData;
                }

                resetEditState();

                if (configuration.rightsAutomationSelected) {
                  scope.model.currentConfiguration = {
                    ...freshConfiguration,
                    rightsAutomationSelected: true,
                  };
                } else {
                  scope.model.currentConfiguration = freshConfiguration;
                }
              })
              .catch((err) => {
                ErrorNotificationService.showDefaultErrorDialog(err);
              })
              .finally(() => {
                scope.model.outstandingAsyncAction = null;
              });

          return returnPromise;
        }

        // Send events to GA for a given action.
        function trackEvent(action: TRACKING_ACTION) {
          TrackingService.recordEvent(
            TrackingService.CATEGORY_RIGHTS_CONFIGURATION,
            action,
          );
        }

        //
        // Scope-bound functions
        //

        scope.model.close = (result: boolean) => {
          scope.close(result);
        };

        scope.model.getFanreelOrCanvasNameFromConfiguration = (
          configuration: any,
        ) => {
          let fanreelOrCanvas;
          if (scope.model.isConfigurationFanreelType(configuration)) {
            fanreelOrCanvas = _(scope.model.fanreelsAndCanvases).findWhere({
              id: configuration.entityId,
            });
            if (fanreelOrCanvas) {
              return (fanreelOrCanvas as IReel).name;
            }

            return configuration.entityId;
          }

          return undefined;
        };

        scope.model.isConfigurationFanreelType = (configuration: any) => {
          const targetType = configuration.targetType || '';

          return targetType.toLowerCase() === FANREEL_TARGET_TYPE.toLowerCase();
        };

        // Either close the modal (if we are in the configuration list view) or
        // Save the current configuration and return to the list view
        scope.model.doneButtonClicked = () => {
          let savePromise;

          if (scope.model.currentView === LIST_VIEW) {
            trackEvent(TRACKING_ACTION.EDIT_MESSAGE_CAMPAIGN);
            scope.model.close();
          } else if (scope.model.currentView === EDIT_VIEW) {
            savePromise = saveCurrentConfiguration();

            if (savePromise) {
              savePromise.then(() => {
                trackEvent(TRACKING_ACTION.EDIT_MESSAGE);
                scope.model.currentView = LIST_VIEW;
              });
            }
          }
          $ngRedux.dispatch(requestRightsActions.requestRightsConfigurations());
        };

        // Handle submit / validation of new Terms and Conditions Modal
        scope.model.saveButtonClicked = () => {
          // TODO: SYND-3440: Consider extracting into linkify.ts as a util.

          if (scope.model.isDefaultUrl) {
            RightsManagementService.updateTermsAndConditions(
              scope.model.url,
              scope.model.isCustom,
            )
              .then(() => {
                // create a new Campaign and configuration set with messages for the client
                if (
                  !scope.model.configurations.some(
                    (obj) => obj.name == 'Bazaarvoice Network',
                  )
                ) {
                  const newBVConfiguration = {
                    name: 'Bazaarvoice Network',
                    isDefault: true,
                    isNew: false,
                    allowCustomRequest: true,
                    targetType: CLIENT_TARGET_TYPE,
                    entityId: null,
                    rightsRequestData: {
                      data: bvNetworkConfig,
                    },
                  };
                  RightsManagementService.updateRightsConfigurationAsync(
                    newBVConfiguration,
                  ).catch((error) => {
                    ErrorNotificationService.showDefaultErrorDialog(error);
                  });
                }
              })
              .then(() => {
                scope.model.termsEditState.errors.hasError = false;
                scope.model.termsEditState.errors.errorMessage = undefined;
                scope.model.currentView = LIST_VIEW;
                scope.model.url = scope.model.constants.DEFAULT_URL;
                scope.model.customUrl = undefined;
                if (!scope.model.isCustom) {
                  scope.model.termsAndConditionsButtonValue =
                    scope.model.constants.TS_AND_CS_VIEW_BUTTON_LABEL;
                } else {
                  scope.model.termsAndConditionsButtonValue =
                    scope.model.constants.TS_AND_CS_EDIT_BUTTON_LABEL;
                }
              })
              .catch((err: unknown) => {
                ErrorNotificationService.showDefaultErrorDialog(err);
                scope.model.termsEditState.errors.hasError = true;
                scope.model.termsEditState.errors.errorMessage = err;
              });
          } else {
            if (isUndefined(scope.model.customUrl)) {
              scope.model.termsEditState.errors.hasError = true;
              scope.model.termsEditState.errors.errorMessage =
                'Please input a URL (http(s)://www.example.com)';
            } else {
              if (isValidUrl(scope.model.customUrl)) {
                RightsManagementService.updateTermsAndConditions(
                  scope.model.customUrl,
                  scope.model.isCustom,
                )
                  .then(() => {
                    scope.model.termsEditState.errors.hasError = false;
                    scope.model.termsEditState.errors.errorMessage = undefined;
                    scope.model.url = scope.model.customUrl;
                    scope.model.termsAndConditionsButtonValue =
                      scope.model.constants.TS_AND_CS_EDIT_BUTTON_LABEL;
                    scope.model.currentView = LIST_VIEW;
                  })
                  .catch((err: IError) => {
                    ErrorNotificationService.showDefaultErrorDialog(err);
                    scope.model.termsEditState.errors.hasError = true;
                    scope.model.termsEditState.errors.errorMessage = err.error
                      .message
                      ? err.error.message
                      : err;
                  });
              } else {
                scope.model.termsEditState.errors.hasError = true;
                scope.model.termsEditState.errors.errorMessage =
                  'Please input a valid URL (http(s)://www.example.com, www.example.com)';
              }
            }
          }
        };

        // Given an existing configuration set, launches the edit view
        scope.model.editConfiguration = (configuration: any) => {
          trackEvent(TRACKING_ACTION.VIEW_MESSAGE_CAMPAGN);
          loadAddEditView(configuration);
        };

        // Removes a configuration from the configuation list view and sends a request to delete it
        scope.model.deleteConfiguration = (configuration: any) => {
          trackEvent(TRACKING_ACTION.DELETE_MESSAGE_CAMPAIGN);
          if (
            configuration &&
            configuration.id &&
            !configuration.isDefault &&
            !configuration.rightsAutomationSelected
          ) {
            scope.model.outstandingAsyncAction = 'delete';
            RightsManagementService.deleteRightsConfigurationAsync(
              configuration.id,
            )
              .then(() => {
                // Remove the just deleted configuration from the array
                scope.model.configurations = _(
                  scope.model.configurations,
                ).reject((config: any) => config.id === configuration.id);

                resetEditState();

                if (scope.model.currentView === EDIT_VIEW) {
                  scope.model.currentView = LIST_VIEW;
                }
              })
              .catch((err: any) => {
                ErrorNotificationService.showDefaultErrorDialog(err);
              })
              .finally(() => {
                scope.model.outstandingAsyncAction = null;
              });
          }
        };

        // Creates a new rights management configuration set and launches the edit view
        scope.model.createNewConfiguration = () => {
          trackEvent(TRACKING_ACTION.CREATE_MESSAGE_CAMPAIGN);

          const newConfiguration = {
            targetType: CLIENT_TARGET_TYPE,
            allowCustomRequest: true,
            rightsRequestData: { data: [] },
            isDefault: false,
            isNew: true,
          };

          loadAddEditView(newConfiguration);
        };

        scope.model.createNewTermsAndConditions = () => {
          loadAddTermsAndConditionsView();
        };

        // Creates a new rights request message on the configuration set being edited
        scope.model.createNewRequestMessage = () => {
          const newMessage = {
            message: null,
            approvalPhrase: null,
            languageCode: Locales.EN_US,
          };

          trackEvent(TRACKING_ACTION.CREATE_MESSAGE_CAMPAIGN);

          if (!scope.model.currentConfiguration.rightsRequestData) {
            scope.model.currentConfiguration.rightsRequestData = { data: [] };
          } else if (!scope.model.currentConfiguration.rightsRequestData.data) {
            scope.model.currentConfiguration.rightsRequestData.data = [];
          }

          scope.model.currentConfiguration.rightsRequestData.data.unshift(
            newMessage,
          );

          scope.model.messageEditState.index = 0;
          scope.model.messageEditState.message = newMessage.message;
          scope.model.messageEditState.approvalPhrase =
            newMessage.approvalPhrase;
          scope.model.messageEditState.isNew = true;
          updateApprovalPhraseState(newMessage.message);
          scope.model.editMessage(null, scope.model.messageEditState.index);
        };

        // Returns true if the message at the given index is currently being edited
        scope.model.isMessageBeingEdited = (index: number) => {
          return (
            scope.model.messageBeingEdited &&
            scope.model.messageEditState.index === index
          );
        };

        // Begins editing the message at the given index
        // Resets any messages currently being edited (only one message can be edited at a given time)
        scope.model.editMessage = ($event: Event, index: number) => {
          let messageToEdit;
          let configListItemElement;
          let totalPadding;
          let pos;
          let scrollingContainer;

          trackEvent(TRACKING_ACTION.VIEW_MESSAGE_CAMPAGN);

          if (
            configurationHasMessages(scope.model.currentConfiguration) &&
            index <=
              scope.model.currentConfiguration.rightsRequestData.data.length - 1
          ) {
            messageToEdit =
              scope.model.currentConfiguration.rightsRequestData.data[index];
          }

          if (messageToEdit) {
            scope.model.messageBeingEdited = true;
            scope.model.messageEditState.index = index;
            // removing the Terms and Conditions URL (if feature flag enabled) when a message is being edited
            if (
              isRightsRequestsTermsAndConditionsEnabled &&
              !isBazaarvoiceNetworkSyndicationEnabled &&
              !scope.model.isDefaultUrl
            ) {
              scope.model.messageEditState.message =
                messageToEdit.message &&
                getMessageWithoutUrl(messageToEdit.message, scope.model.url);
            } else if (
              isRightsRequestsTermsAndConditionsEnabled &&
              !isBazaarvoiceNetworkSyndicationEnabled &&
              scope.model.isDefaultUrl
            ) {
              scope.model.setTermsAndConditionsUrl(messageToEdit.languageCode);
              scope.model.messageEditState.message =
                messageToEdit.message &&
                getMessageWithoutUrl(
                  messageToEdit.message,
                  scope.model.tandcUrl,
                );
            } else if (isSyndicationAndTermsConditionsEnabled) {
              scope.model.setTermsAndConditionsUrl(messageToEdit.languageCode);
              const originalMsg = messageToEdit.message;
              const selectedLangRetailerMessages = scope.model.retailerMessages;
              const retailerMsg = selectedLangRetailerMessages.filter(
                (el: any) => originalMsg.includes(el),
              );
              const msgWithoutUrl =
                messageToEdit.message &&
                getMessageWithoutUrl(
                  messageToEdit.message,
                  scope.model.tandcUrl,
                );
              scope.model.messageEditState.message = msgWithoutUrl
                .split(retailerMsg[0])[0]
                ?.trim();
            } else {
              scope.model.messageEditState.message = messageToEdit.message;
            }
            scope.model.messageEditState.approvalPhrase =
              messageToEdit.approvalPhrase;
            updateApprovalPhraseState(messageToEdit.message);
          } else {
            scope.model.setTermsAndConditionsUrl(messageToEdit.languageCode);
          }

          if ($event && $event.target) {
            configListItemElement = angular
              .element($event.target)
              .closest('.configuration-list-item');
            const innerPadding =
              parseInt(
                angular.element('.modal-body-inner').css('padding'),
                10,
              ) * 2;
            totalPadding =
              innerPadding +
              angular.element('.configuration-wide-button').outerHeight();
            scrollingContainer = configListItemElement.parent();

            pos =
              configListItemElement.position().top +
              scrollingContainer.scrollTop();

            ScrollAnimationService.scroll(
              scrollingContainer,
              pos - totalPadding,
            );
          }
        };

        scope.model.deleteMessage = (index) => {
          if (
            scope.model?.currentConfiguration?.rightsAutomationSelected &&
            scope.model.selectedMessagesCount <
              scope.model.constants.DEFAULT_MESSAGE_LIMIT
          ) {
            return;
          }
          if (
            scope.model.currentConfiguration.rightsRequestData.data.length > 1
          ) {
            trackEvent(TRACKING_ACTION.DELETE_MESSAGE);

            scope.model.currentConfiguration.rightsRequestData.data.splice(
              index,
              1,
            );

            if (scope.model.currentConfiguration.rightsAutomationSelected) {
              scope.model.selectedMessagesCount =
                scope.model.selectedMessagesCount - 1;
            }
            saveCurrentConfiguration();
          }
        };

        // Validates and persists the current message edits
        scope.model.saveMessageEdit = () => {
          const configuration = scope.model.currentConfiguration;
          const errorsFound = setMessageErrors();
          const retailerMessages = scope.model.retailerMessages;
          const randomRetailMsg =
            retailerMessages[
              Math.floor(Math.random() * retailerMessages.length)
            ];
          if (configuration.rightsAutomationSelected) {
            scope.model.selectedMessagesCount =
              scope.model.selectedMessagesCount + 1;
          }
          if (configuration && !errorsFound) {
            configuration.rightsRequestData.data[
              scope.model.messageEditState.index
            ] = {
              // adding Terms and Conditions URL & retailer Message back in when saving (if feature flag is enabled) or the request will fail
              message: getAppropriateConfigurationMessage(),
              approvalPhrase: scope.model.messageEditState.approvalPhrase,
              languageCode: scope.model.languageCode,
            };
            saveCurrentConfiguration();
          }

          function getAppropriateConfigurationMessage() {
            if (
              isRightsRequestsTermsAndConditionsEnabled &&
              !isBazaarvoiceNetworkSyndicationEnabled &&
              scope.model.isDefaultUrl
            ) {
              return `${scope.model.messageEditState.message} ${scope.model.tandcUrl}`;
            }

            if (
              isRightsRequestsTermsAndConditionsEnabled &&
              !isBazaarvoiceNetworkSyndicationEnabled &&
              !scope.model.isDefaultUrl
            ) {
              return `${scope.model.messageEditState.message} ${scope.model.url}`;
            }

            if (isSyndicationAndTermsConditionsEnabled) {
              return `${scope.model.messageEditState.message} ${randomRetailMsg} ${scope.model.tandcUrl}`;
            }

            return scope.model.messageEditState.message;
          }
        };

        // Cancels an active message edit
        scope.model.exitMessageEdit = () => {
          if (scope.model.messageEditState.isNew) {
            scope.model.currentConfiguration.rightsRequestData.data.shift();
          }

          resetEditState();
        };

        // Opens the configuration name edit field and focuses the input
        scope.model.openConfigurationNameEditField = () => {
          scope.model.editState.editingConfigurationName = true;

          // Automatically focus on the name-edit box. This logic is required here because the 'autofocus' attribute
          // in the html only works upon the creation of an element. So closing the modal and re-opening it results
          // in the input not having focus. We clear the value and re-add it to force the cursor to the end of the
          // textarea.
          $timeout(() => {
            const nameEditField = angular
              .element(element)
              .find('.edit-name-input');
            const existingText = nameEditField.val();

            nameEditField.focus().val('').val(existingText);
          }, 0);
        };

        scope.model.onConfigurationNameKeypress = ($event) => {
          if ($event.which === ENTER_KEY_CODE) {
            scope.model.closeConfigurationNameEditField();
          }
        };

        // Closes the configuration name edit field
        scope.model.closeConfigurationNameEditField = () => {
          scope.model.editState.editingConfigurationName = false;
        };

        // Cancels an active configuration edit
        scope.model.cancelConfigurationEdit = () => {
          scope.model.exitMessageEdit();
          scope.model.currentView = LIST_VIEW;
        };

        // Cancel Terms and Conditions edit
        scope.model.cancelTermsEdit = () => {
          scope.model.url = null;
          scope.model.isCustom = null;
          RightsManagementService.getTermsAndConditions()
            .then((response) => {
              scope.model.isTandCSetup = true;
              scope.model.oldTandCurl =
                response.data.termsAndConditions.termsAndConditionsUrl;
              scope.model.url =
                response.data.termsAndConditions.termsAndConditionsUrl;
              scope.model.isCustom = response.data.termsAndConditions.isCustom;
              if (!scope.model.isCustom) {
                scope.model.termsAndConditionsButtonValue =
                  scope.model.constants.TS_AND_CS_VIEW_BUTTON_LABEL;
              } else {
                scope.model.termsAndConditionsButtonValue =
                  scope.model.constants.TS_AND_CS_EDIT_BUTTON_LABEL;
              }
            })
            .catch((err: unknown) => {
              ErrorNotificationService.showDefaultErrorDialog(err);
            });
          scope.model.currentView = LIST_VIEW;
          scope.model.termsEditState.errors.hasError = false;
          scope.model.termsEditState.errors.errorMessage = undefined;
        };

        // Callback for the <dropdown /> directive which is called whenever a new <dropdown-option /> is selected
        scope.model.setApprovalPhrase = (approvalPhrase: string) => {
          scope.model.messageEditState.approvalPhrase = approvalPhrase;
        };

        // Sets the fanreel the current configuration is associated with
        scope.model.setFanreel = (fanreel: IReel) => {
          if (fanreel) {
            scope.model.editState.selectedFanreel = fanreel;
          }
        };

        // Callback for the termsAndConditionsUrlSelect <dropdown /> directive which is called whenever a new <dropdown-option /> is selected
        scope.model.setTermsAndConditionsUrl = (
          languageCode: string = Locales.EN_US,
        ) => {
          scope.model.languageCode = languageCode;
          scope.model.tandcUrl =
            scope.model.termsConditionsSettings[languageCode].url;
          scope.model.retailerMessages =
            scope.model.termsConditionsSettings[languageCode].retailerMessages;
        };

        // Watch the message being edited and update the approval phrase state based on the number of hashtags contained
        // within the message. This method is debounced so it's not fired on every keydown event. We need to
        // scope.$apply because _.debounce fires outside of Angular-land
        scope.$watch(
          'model.messageEditState.message',
          _.debounce((newVal: string, oldVal: string) => {
            if (newVal && !angular.equals(newVal, oldVal)) {
              updateApprovalPhraseState(newVal);
              scope.$apply();
            }
          }, 100),
        );

        //
        // Initialize
        //
        const { selectedToggleValue, selectedCompaigns } =
          $ngRedux.getState().accountSettings.rightsManagement
            .automateRightsRequests;
        scope.model.outstandingAsyncAction = 'init';

        $q.all([
          RightsManagementService.getRightsConfigurationsAsync(),
          FanreelService.getReelsAsync({
            noStats: true,
            includeModeratedCanvases: true,
          }),
        ])
          .then((response) => {
            let selectedMsgCount = 0;
            const configurationsObj = response[0];

            configurationsObj.forEach((campaign: any) => {
              if (
                selectedToggleValue &&
                selectedCompaigns.includes(campaign.id)
              ) {
                campaign.rightsAutomationSelected = true;
                selectedMsgCount += campaign.rightsRequestData.data.length;
              } else {
                campaign.rightsAutomationSelected = false;
              }
            });
            scope.model.configurations = configurationsObj;
            scope.model.selectedMessagesCount = selectedMsgCount;
            scope.model.fanreelsAndCanvases = response[1];
            const fanreelsAndCanvases = _(response[1]).partition(
              (fanreelOrCanvas: any) => !fanreelOrCanvas.isCanvas,
            );
            scope.model.fanreels = fanreelsAndCanvases[0];
            scope.model.canvases = fanreelsAndCanvases[1];

            _(scope.model.configurations).each((configuration) => {
              (configuration as any).isNew = false;
            });
          })
          .catch((err) => {
            ErrorNotificationService.showDefaultErrorDialog(err);
          })
          .finally(() => {
            scope.model.loading = false;
            scope.model.outstandingAsyncAction = null;
          });

        if (isRightsRequestsTermsAndConditionsEnabled) {
          return RightsManagementService.getTermsAndConditions()
            .then((response: any) => {
              scope.model.isTandCSetup = true;
              scope.model.oldTandCurl =
                response.data.termsAndConditions.termsAndConditionsUrl;
              scope.model.url =
                response.data.termsAndConditions.termsAndConditionsUrl;
              scope.model.isCustom = response.data.termsAndConditions.isCustom;

              if (
                response.data.termsAndConditions.termsAndConditionsUrl !==
                scope.model.constants.DEFAULT_URL
              ) {
                scope.model.storedUrl =
                  response.data.termsAndConditions.termsAndConditionsUrl;
                scope.model.customUrl =
                  response.data.termsAndConditions.termsAndConditionsUrl;
              }

              if (isDefined(scope.model.url)) {
                scope.model.termsAndConditionsButtonValue =
                  scope.model.constants.TS_AND_CS_EDIT_BUTTON_LABEL;
              }
              if (!scope.model.isCustom) {
                scope.model.termsAndConditionsButtonValue =
                  scope.model.constants.TS_AND_CS_VIEW_BUTTON_LABEL;
              }
            })
            .catch((err: unknown) => {
              ErrorNotificationService.showDefaultErrorDialog(err);
            });
        }
      },
    };
  },
]);
